import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EntradasService } from './entradas.service';
import { VentaService } from './venta.service';

export class MotivoSeleccionado {
  idEntrada: string;
  lista: any[];
  constructor(idEntrada) {
    this.idEntrada = idEntrada;
    this.lista = [];
  }
}

@Injectable({
  providedIn: 'root'
})
export class MotivosService {
  private motivosSubject: BehaviorSubject<any>;
  public motivos: Observable<any>;

  private motivosSeleccionadosSubject: BehaviorSubject<any>;
  public motivosSeleccionados: Observable<any>;

  private entradasDesdeLocalizadorConMotivoSubject: BehaviorSubject<any>;
  public entradasDesdeLocalizadorConMotivo: Observable<any>;

  constructor(private entradaService: EntradasService, private ventaService: VentaService) {
    this.motivosSubject = new BehaviorSubject(undefined);
    this.motivos = this.motivosSubject.asObservable();

    this.motivosSeleccionadosSubject = new BehaviorSubject([]);
    this.motivosSeleccionados = this.motivosSeleccionadosSubject.asObservable();

    this.entradasDesdeLocalizadorConMotivoSubject = new BehaviorSubject([]);
    this.entradasDesdeLocalizadorConMotivo = this.entradasDesdeLocalizadorConMotivoSubject.asObservable();
  }

  public get motivosValue() {
    return this.motivosSubject.value;
  }

  setMotivosValue(motivos) {
    this.motivosSubject.next(motivos);
  }

  public get getAllMotivosSeleccionados() {
    return this.motivosSeleccionadosSubject.asObservable();
  }

  public get entradasDesdeLocalizadorConMotivoValue() {
    return this.entradasDesdeLocalizadorConMotivoSubject.value;
  }

  public setEntradasDesdeLocalizadorConMotivo(entradas) {
    this.entradasDesdeLocalizadorConMotivoSubject.next(entradas);
  }

  public getMotivosSeleccionadosByEntradaId(id) {
    return this.motivosSeleccionadosSubject.asObservable().pipe(
      map(motivos => {
        return motivos.find(motivo => motivo.idEntrada === id);
      })
    );
  }

  public getDistinctCountMotivosByEntradaId(id) {
    return this.motivosSeleccionadosSubject.asObservable().pipe(
      map(motivos => {
        if (motivos.find(motivo => motivo.idEntrada === id)) {
          return motivos
            .find(motivo => motivo.idEntrada === id)
            .lista.map(m => {
              return { id: m.PkId, nombre: m.Nombre, qty: m.numMotivo };
            });
        } else {
          return [];
        }
      })
    );
  }

  public pushMotivo(motivo, cantidad: any) {
    if (cantidad === 0) {
      this.deleteMotivo(motivo);
      this.getCantidadTotalPorEntrada();
      return;
    }

    const motivos = this.motivosSeleccionadosSubject.value;

    let linea;
    if (
      this.motivosSeleccionadosSubject.value.find(m => m.idEntrada === motivo.idEntradaAsociada)
    ) {
      linea = this.motivosSeleccionadosSubject.value.find(
        m => m.idEntrada === motivo.idEntradaAsociada
      );
    } else {
      linea = new MotivoSeleccionado(motivo.idEntradaAsociada);
      motivos.push(linea);
    }

    if (linea.lista.find(m => m.PkId === motivo.PkId)) {
      linea.lista.find(m => m.PkId === motivo.PkId).numMotivo = cantidad;
    } else {
      linea.lista.push({ ...motivo, numMotivo: cantidad });
    }

    this.setMotivosSeleccionados(motivos);
    this.getCantidadTotalPorEntrada();
  }

  private deleteMotivo(motivo) {
    const motivos = this.motivosSeleccionadosSubject.value;
    const linea = motivos.find(m => m.idEntrada === motivo.idEntradaAsociada);

    if (linea) {
      motivos.find(m => m.idEntrada === motivo.idEntradaAsociada).lista = linea.lista.filter(
        m => m.PkId !== motivo.PkId
      );
    }
    const lineaAEliminar = motivos.find(m => m.idEntrada === motivo.idEntradaAsociada);
    if (lineaAEliminar && lineaAEliminar.lista.length === 0) {
      const index = motivos.findIndex(m => m.idEntrada === motivo.idEntradaAsociada);
      if (index !== -1) {
        motivos.splice(index, 1);
      }
    }
    this.setMotivosSeleccionados(motivos);
  }

  setMotivosSeleccionados(motivosSeleccionados) {
    this.motivosSeleccionadosSubject.next(motivosSeleccionados);
  }

  /** Quita motivos guardados para una tarifa/entrada (p. ej. al borrar la línea del resumen). */
  removeMotivosParaEntrada(idTarifa: string | number | undefined | null): void {
    if (idTarifa === undefined || idTarifa === null || idTarifa === '') {
      return;
    }
    const id = String(idTarifa);
    const actual = this.motivosSeleccionadosSubject.value || [];
    const filtrado = actual.filter((m) => m && String(m.idEntrada) !== id);
    if (filtrado.length !== actual.length) {
      this.setMotivosSeleccionados(filtrado);
    }
  }

  /**
   * Tras borrar unidades en resumen sin eliminar la línea: alinear motivosSeleccionados
   * y ListaMotivos con la cantidad restante del carrito.
   */
  ajustarMotivosTrasEliminarParcial(
    idTarifa: string | number | undefined | null,
    cantidadRestante: number,
    entrada?: any
  ): void {
    if (idTarifa === undefined || idTarifa === null || idTarifa === '' || cantidadRestante < 0) {
      return;
    }
    const id = String(idTarifa);
    const motivos = [...(this.motivosSeleccionadosSubject.value || [])];
    const linea = motivos.find((m) => m && String(m.idEntrada) === id);
    if (!linea?.lista?.length) {
      if (entrada?.ListaMotivos?.length === 1 && cantidadRestante >= 0) {
        entrada.ListaMotivos[0].Cantidad = cantidadRestante;
      }
      return;
    }

    const totalAntes = linea.lista.reduce((s, m) => s + (Number(m.numMotivo) || 0), 0);

    if (linea.lista.length === 1) {
      linea.lista[0].numMotivo = cantidadRestante;
    } else if (totalAntes > cantidadRestante) {
      let restante = totalAntes - cantidadRestante;
      for (let i = linea.lista.length - 1; i >= 0 && restante > 0; i--) {
        const item = linea.lista[i];
        const qty = Number(item.numMotivo) || 0;
        const quitar = Math.min(qty, restante);
        item.numMotivo = qty - quitar;
        restante -= quitar;
        if (item.numMotivo <= 0) {
          linea.lista.splice(i, 1);
        }
      }
    }

    if (linea.lista.length === 0) {
      const idx = motivos.findIndex((m) => m && String(m.idEntrada) === id);
      if (idx >= 0) {
        motivos.splice(idx, 1);
      }
    }

    this.setMotivosSeleccionados(motivos);

    if (entrada) {
      const lineaFinal = motivos.find((m) => m && String(m.idEntrada) === id);
      entrada.ListaMotivos = lineaFinal?.lista?.length
        ? lineaFinal.lista.map((motivo) => ({
            MotivoId: motivo.PkId,
            Cantidad: motivo.numMotivo,
          }))
        : [];
    }
  }

  public get motivosSeleccionadosValue() {
    return this.motivosSeleccionadosSubject.value;
  }

  private getCantidadTotalPorEntrada() {
    const entradaSeleccionada = this.entradaService.entradaSeleccionada;
    const motivos = this.motivosSeleccionadosValue.find(
      m => m.idEntrada === entradaSeleccionada.idTarifa
    );
    if (!motivos || motivos.lista.length === 0) {
      this.ventaService.sendEntradas(entradaSeleccionada, 0);
      return;
    }
    const cantidad = motivos.lista
      .map(element => element.numMotivo)
      .reduce((total, num) => total + num);
    this.ventaService.sendEntradas(entradaSeleccionada, cantidad);
  }

  getCantidadTotal() {
    const entradaSeleccionada = this.entradaService.entradaSeleccionada;
    const motivos = this.motivosSeleccionadosValue.find(
      m => m.idEntrada === entradaSeleccionada.idTarifa
    );
    if (!motivos || motivos.lista.length === 0) {
      return 0;
    }
    const cantidad = motivos.lista
      .map(element => element.numMotivo)
      .reduce((total, num) => total + num);
    return cantidad;
  }

  getPrecioTotal() {
    const entradaSeleccionada = this.entradaService.entradaSeleccionada;
    const motivos = this.motivosSeleccionadosValue.find(
      m => m.idEntrada === entradaSeleccionada.idTarifa
    );
    if (!motivos || motivos.lista.length === 0) {
      return 0;
    }
    const cantidad = motivos.lista
      .map(element => element.numMotivo)
      .reduce((total, num) => total + num);
    // Keep modal total aligned with the rest of sale UI:
    // unit total = PrecioEnDivisaReferencia + Comision
    const precioReferencia = Number.parseFloat(
      String(entradaSeleccionada.PrecioEnDivisaReferencia || '0').replace(/,/, '.')
    );
    const comision = Number.parseFloat(
      String(entradaSeleccionada.Comision || '0').replace(/,/, '.')
    );
    const unitTotal = precioReferencia + comision;
    return Math.round(unitTotal * cantidad * 100) / 100;
  }
}
